import * as HrActions from '../actions/hr.action';
import {
  IDashboardApplication,
  IDashboardFetchApplicationPayload,
  IHrApplication,
} from '../../shared/interfaces/hr.interface';
import { IQuery } from '../../shared/interfaces/api-query.interface';
import { IAuditTrail } from '../../shared/interfaces/common.interface';

interface IHrState {
  list: {
    count: number | null;
    current_page: number | null;
    items: IHrApplication[];
    pages: number | null;
    total: number | null;
  };
  /* eslint-disable camelcase */
  is_loading_approval_list: boolean;
  approval_list: {
    total: number | null;
    count: number | null;
    current_page: number | null;
    pages: number | null;
    ApprovalsReport: {
      approvals: {
        application: IDashboardApplication[];
        company: string;
        created_at: string;
        day_processed: number | null;
        department: string;
        position: string;
        remarks: string;
        signatory: string;
        signatory_email: string;
        slug: string;
        status: string;
        updated_at: string;
      }[];
      counts: {
        Approved: number | null;
        Pending: number | null;
        Total: number | null;
      };
    };
  };
  // reports query
  is_loading_dashboard_list: boolean;
  dashboard_list: {
    count: number | null;
    current_page: number | null;
    ApplicationsReport: {
      ReasonBreakdown: {
        breakdown: { [key: string]: number };
        total: number | null;
      };
      SignatoryBreakdown: {
        breakdown: { signatory: string; approved: number | null; pending: number | null }[];
      };
      applications: IDashboardApplication[];
      counts: {
        Completed: number | null;
        Pending: number | null;
        TAT: number | null;
        Total: number | null;
        Top3Reasons: { [key: string]: number };
      };
    };
    pages: number | null;
    total: number | null;
  };
  reports_query: IDashboardFetchApplicationPayload;

  // reports: define interface
  is_loading_reports: boolean;

  reports: {
    departments: string[];
    provinces: string[];
    reasons: string[];
    years: number[];
  };
  selected: {
    application: any;
    history: Array<IAuditTrail> | null;
  };
  query: IQuery;
}

const initialState: IHrState = {
  /* eslint-disable camelcase */
  list: {
    count: null,
    current_page: null,
    items: [],
    pages: null,
    total: null,
  },
  is_loading_approval_list: false,
  approval_list: {
    total: null,
    count: null,
    current_page: null,
    pages: null,
    ApprovalsReport: {
      approvals: [],
      counts: {
        Approved: null,
        Pending: null,
        Total: null,
      },
    },
  },
  // reports query
  is_loading_dashboard_list: false,
  dashboard_list: {
    count: null,
    current_page: null,
    ApplicationsReport: {
      ReasonBreakdown: {
        breakdown: {},
        total: null,
      },
      SignatoryBreakdown: {
        breakdown: [{ signatory: '', approved: null, pending: null }],
      },
      applications: [],
      counts: {
        Completed: null,
        Pending: null,
        TAT: null,
        Total: null,
        Top3Reasons: {},
      },
    },
    pages: null,
    total: null,
  },

  // reports: define interface
  is_loading_reports: false,
  reports: {
    departments: [],
    provinces: [],
    reasons: [],
    years: [],
  },
  selected: {
    application: null,
    history: null,
  },
  reports_query: {
    page: 1,
    limit: 10,
    q: '',
    order_by: '',
    department: '',
    month: '',
    province: '',
    reason: '',
    signatory: '',
    status: '',
    year: '',
    is_export: '',
  },
  query: {
    page: 1,
    limit: 10,
    q: '',
    order_by: '',
  },
};

const reducer = (state = initialState, action: { type: string; payload: any }) => {
  const { type, payload } = action;
  switch (type) {
    case HrActions.HR_FETCH_APPLICATIONS:
      return {
        ...state,
        query: payload,
      };

    case HrActions.HR_FETCH_APPLICATIONS_SUCCESS:
      return {
        ...state,
        list: payload,
      };

    case HrActions.HR_FETCH_APPLICATIONS_ERROR:
      return {
        ...state,
      };

    case HrActions.HR_FETCH_APPLICATION_INFORMATION:
      return {
        ...state,
      };

    case HrActions.HR_FETCH_APPLICATION_INFORMATION_SUCCESS:
      return {
        ...state,
        selected: payload,
      };

    case HrActions.HR_FETCH_APPLICATION_INFORMATION_ERROR:
      return {
        ...state,
      };

    case HrActions.HR_EDIT_APPLICATION_INFORMATION:
      return {
        ...state,
      };

    case HrActions.HR_EDIT_APPLICATION_INFORMATION_SUCCESS:
      return {
        ...state,
        selected: payload,
      };

    case HrActions.HR_EDIT_APPLICATION_INFORMATION_ERROR:
      return {
        ...state,
      };

    case HrActions.HR_DELETE_APPLICATION:
      return {
        ...state,
        query: payload,
      };

    case HrActions.HR_DELETE_APPLICATION_SUCCESS: {
      const newList = [...state.list.items].filter((e: any) => e.slug !== payload);

      return {
        ...state,
        list: {
          count: state.list.count,
          current_page: state.list.current_page,
          items: newList,
          pages: state.list.pages,
          total: state.list.total,
        },
      };
    }

    case HrActions.HR_DELETE_APPLICATION_ERROR:
      return {
        ...state,
      };

    case HrActions.HR_ADD_APPROVER:
      return {
        ...state,
      };

    case HrActions.HR_ADD_APPROVER_SUCCESS: {
      const selected = { ...state.selected };
      const newSignatoryList = {
        ...selected.application,
        ...selected,
        application: {
          ...selected.application,
          signatory_remarks: payload,
        },
      };

      return {
        ...state,
        selected: newSignatoryList,
      };
    }

    case HrActions.HR_ADD_APPROVER_ERROR:
      return {
        ...state,
      };

    case HrActions.HR_DELETE_APPROVER:
      return {
        ...state,
      };

    case HrActions.HR_DELETE_APPROVER_SUCCESS: {
      const selected = { ...state.selected };
      const { signatory_remarks } = selected.application;
      const newList = [...signatory_remarks].filter((i: any) => i.slug !== payload);

      const newSelected = {
        ...selected,
        application: {
          ...selected.application,
          signatory_remarks: newList,
        },
      };

      return {
        ...state,
        selected: newSelected,
      };
    }

    case HrActions.HR_DELETE_APPROVER_ERROR:
      return {
        ...state,
      };

    case HrActions.DASHBOARD_FETCH_STATIC_DATA:
      return {
        ...state,
        is_loading_reports: true,
      };

    case HrActions.DASHBOARD_FETCH_STATIC_DATA_SUCCESS:
      return {
        ...state,
        reports: payload,
        is_loading_reports: false,
      };

    case HrActions.DASHBOARD_FETCH_STATIC_DATA_ERROR:
      return {
        ...state,
        is_loading_reports: false,
      };

    case HrActions.DASHBOARD_FETCH_APPLICATION_DATA:
      return {
        ...state,
        reports_query: payload,
        is_loading_dashboard_list: true,
      };

    case HrActions.DASHBOARD_FETCH_APPLICATION_DATA_SUCCESS:
      return {
        ...state,
        dashboard_list: payload,
        is_loading_dashboard_list: false,
      };

    case HrActions.DASHBOARD_FETCH_APPLICATION_DATA_ERROR:
      return {
        ...state,
        is_loading_dashboard_list: false,
      };

    case HrActions.DASHBOARD_FETCH_APPROVAL_DATA:
      return {
        ...state,
        reports_query: payload,
        is_loading_approval_list: true,
      };

    case HrActions.DASHBOARD_FETCH_APPROVAL_DATA_SUCCESS:
      return {
        ...state,
        approval_list: payload,
        is_loading_approval_list: false,
      };

    case HrActions.DASHBOARD_FETCH_APPROVAL_DATA_ERROR:
      return {
        ...state,
        is_loading_approval_list: false,
      };

    default:
      return state;
  }
};

export default reducer;
